import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";

import {
  CaseAdmin,
  CaseSpecimenAdmin,
  Icd10Code,
  Lab,
  SpecimenAdmin,
  SpecimenProtocolOverride,
} from "pwapi";

import {
  updateSpecimen,
  createSpecimen,
} from "shared/actions/admin/adminSpecimenActions";
import { addToast, nextId } from "shared/actions/toastActions";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import {
  getProtocolStatuses,
  procedureEnumToLabel,
  protocolLabels,
} from "shared/utils/specimenUtils";

import { AdminFieldIcd10Code } from "components/admin/edit/fields";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import {
  initialValuesForSpecimen,
  shallowSpecimenComparison,
  submissionFieldsForSpecimen,
} from "./specimenFormUtils";

type SpecimenFormProps = {
  specimen?: SpecimenAdmin | CaseSpecimenAdmin;
  adminCase: CaseAdminDetail;
  onClose: Function;
  setDelSpecimen: Function;
  disableSubmit?: boolean;
};

type GrossDescFindReplace = {
  message: string;
  updated: boolean;
};

const SpecimenFormProtocols = ({
  specimen,
  setFieldValue,
  accessioningLab,
}: {
  specimen: SpecimenAdmin | CaseSpecimenAdmin;
  setFieldValue: Function;
  accessioningLab?: Lab;
}) => {
  const protocolOverrides =
    (specimen as SpecimenAdmin).protocol_overrides || [];
  const protocolStatuses = getProtocolStatuses(
    specimen,
    true,
    accessioningLab?.settings
  );

  const setProtocolOverride = (
    p: SpecimenProtocolOverride.protocol,
    v: string
  ) => {
    const newProtocolOverrides = protocolOverrides.filter(
      (po) => po.protocol !== p
    );

    if (v !== "null") {
      newProtocolOverrides.push({ protocol: p, active: v === "true" });
    }

    setFieldValue("protocol_overrides", newProtocolOverrides);
  };

  const protocolsStr = Object.entries(
    getProtocolStatuses(specimen, undefined, accessioningLab?.settings)
  )
    .filter(([_, status]) => status.active)
    .map(([protocol, _]) => protocol)
    .join(",");
  const initialProtocolsStr = useMemo(() => protocolsStr, []);

  const protocolsToShow = ["alopecia", "nail" /* "whole_specimen" */];

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );
  if (difEnabled) protocolsToShow.push("dif");

  const pashReflexEnabled = accessioningLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = accessioningLab?.settings?.pasf_reflex?.active;

  if (pashReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASH_REFLEX);

  if (pasfReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASF_REFLEX);

  const slowMohsEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.slowMohsProtocol
  );
  if (slowMohsEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.SLOW_MOHS);

  return (
    <fieldset className="panelContainer__section">
      <legend className="panelContainer__section--title">Protocols</legend>
      {protocolsToShow.map((p) => (
        <div style={{ display: "flex" }} key={p}>
          <div
            className={classNames("Tag", {
              "Tag--blue":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                protocolStatuses[p as SpecimenProtocolOverride.protocol].active,
              "Tag--gray":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                !protocolStatuses[p as SpecimenProtocolOverride.protocol]
                  .active,
              "Tag--blue--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === true,
              "Tag--gray--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === false,
            })}
            data-tip={
              protocolStatuses[p as SpecimenProtocolOverride.protocol].reason
            }
            data-for="specimen-form-protocols"
          >
            {protocolLabels[p as SpecimenProtocolOverride.protocol]}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <select
              onChange={(ev) =>
                setProtocolOverride(
                  p as SpecimenProtocolOverride.protocol,
                  ev.currentTarget.value
                )
              }
              value={
                protocolOverrides.find((po) => po.protocol === p) === undefined
                  ? "null"
                  : protocolOverrides.find((po) => po.protocol === p)?.active
                  ? "true"
                  : "false"
              }
            >
              <option value="null">
                Auto:{" "}
                {protocolStatuses[p as SpecimenProtocolOverride.protocol].active
                  ? "ON"
                  : "OFF"}
              </option>
              <option value="true">Override: ON</option>
              <option value="false">Override: OFF</option>
            </select>
          </div>
        </div>
      ))}
      {protocolsStr !== initialProtocolsStr && (
        <div style={{ fontStyle: "italic", color: "darkred" }}>
          Changing Protocols after Accessioning is not fully supported.
        </div>
      )}
      <ReactTooltip id="specimen-form-protocols" place="right" />
    </fieldset>
  );
};

interface ProcedureOption {
  label: string;
  value: SpecimenAdmin.procedure;
  disabled: boolean;
}
const createProcedureOption = (p: SpecimenAdmin.procedure) => ({
  label: procedureEnumToLabel(p) || "",
  value: p,
  disabled: p === SpecimenAdmin.procedure.U,
});

const SpecimenForm = ({
  specimen,
  adminCase,
  onClose,
  setDelSpecimen,
  disableSubmit = false,
}: SpecimenFormProps) => {
  const dispatch = useDispatch();
  const [grossDescriptionDisabled, setGrossDescriptionDisabled] =
    useState(false);
  const [keepEditing, setKeepEditing] = useState(false);

  const initialValues = initialValuesForSpecimen(
    adminCase,
    specimen || { name: "", protocol_overrides: [] }
  );

  // Reset anything here when the user saves but keeps the panel open
  const handleOnSaveAndKeepOpen = () => {
    setGrossDescriptionDisabled(false);
  };

  const handleOnSubmit = (values: Partial<CaseSpecimenAdmin>) => {
    if (disableSubmit) return;

    specimen?.id
      ? dispatch(
          updateSpecimen({
            specimen: {
              ...shallowSpecimenComparison(adminCase, specimen, values),
              id: specimen?.id,
            },
            case_id: adminCase.id,
            onClose: !keepEditing ? onClose : handleOnSaveAndKeepOpen(),
            onGrossDescriptionUpdate: (
              grossDescFindReplace: GrossDescFindReplace
            ) => {
              const { updated, message } = grossDescFindReplace;
              const toastId = nextId();

              // If the gross description is disabled, that means site was edited.
              // Show a toast message to the user with gross description update status.
              if (grossDescriptionDisabled) {
                dispatch(
                  addToast(
                    {
                      kind: updated ? "success" : "warning",
                      message,
                      removalTimeout: 2000,
                    },
                    toastId
                  )
                );
              }
            },
          })
        )
      : dispatch(
          createSpecimen({
            requestBody: {
              ...submissionFieldsForSpecimen(adminCase, specimen, values),
              name: values?.name || "",
              case_id: adminCase?.id,
              case: adminCase?.id || 0,
              protocol_overrides: values.protocol_overrides || [],
            },
          })
        ).then(() => {
          if (!keepEditing) {
            onClose();
          } else {
            handleOnSaveAndKeepOpen();
          }
        });
  };

  // Set aside Procedure Code `O` - "Other".  It will be filtered out of the available pool of Codes, and re-appended after
  const procedureCodeOther = SpecimenAdmin.procedure.O;

  const prepareProcedureOptions = () =>
    // Begin with array of global Specimen Admin Procedure codes
    Object.values(SpecimenAdmin.procedure)
      // Trim out non-applicable Procedure codes and Omit `Other` for now.  It will be appended to the end of the sorted array.
      .filter(
        (v) =>
          (specimen?.procedure === SpecimenAdmin.procedure.U ||
            v !== SpecimenAdmin.procedure.U) &&
          v !== procedureCodeOther
      )
      // Transform Procedure Codes into Procedure Option objects
      .map<ProcedureOption>((p) => createProcedureOption(p))
      // Sort Array of Procedure Options by `label`
      .sort((a, b) => a.label.localeCompare(b.label));

  const procedureOptions = useMemo(
    () => [
      ...prepareProcedureOptions(),
      // Reinsert Procedure Option object "Other" at the end of the array
      createProcedureOption(procedureCodeOther),
    ],
    []
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => handleOnSubmit(values)}
    >
      {({ handleChange, resetForm, dirty, values, setFieldValue }) => (
        <Form className="AdminPanelForm">
          <div className="panelContainer">
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Specimen Details
              </legend>
              <Field name="name">
                {({ field }: any) => (
                  <TextInput
                    label="Name"
                    id="name"
                    {...field}
                    disabled={disableSubmit}
                    required={!specimen?.id}
                  />
                )}
              </Field>
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Specimen Information
              </legend>

              {adminCase.source === CaseAdmin.source.PW ? (
                <>
                  <Field name="type">
                    {({ field }: any) => (
                      <SelectInput
                        label="Specimen Type"
                        options={Object.values(SpecimenAdmin.type).map((a) => ({
                          label: a,
                          value: a,
                        }))}
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="site">
                    {({ field }: any) => (
                      <TextInput
                        label="Site"
                        id="site"
                        {...field}
                        onChange={(e: any) => {
                          // If the site is edited, disable gross description editing
                          if (!!values.gross_description?.length) {
                            setGrossDescriptionDisabled(true);
                          }
                          handleChange(e);
                        }}
                        disabled={disableSubmit}
                        spellCheck
                      />
                    )}
                  </Field>
                  <Field name="procedure">
                    {({ field }: any) => (
                      <SelectInput
                        label="Procedure"
                        id="specimen__procedure"
                        options={procedureOptions}
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <Field name="source">
                  {({ field }: any) => (
                    <TextInput
                      label="Source"
                      id="source"
                      {...field}
                      disabled={disableSubmit}
                      spellCheck
                    />
                  )}
                </Field>
              )}

              <Field name="clinical_history">
                {({ field }: any) => (
                  <TextareaInput
                    label="Clinical History"
                    id="clinical_history"
                    rows={4}
                    {...field}
                    disabled={disableSubmit}
                  />
                )}
              </Field>
              <Field name="gross_description">
                {({ field }: any) => (
                  <TextareaInput
                    label="Gross Description"
                    id="gross_description"
                    rows={4}
                    {...field}
                    disabled={disableSubmit || grossDescriptionDisabled}
                  />
                )}
              </Field>
              {grossDescriptionDisabled && (
                <p className="panelContainer__section--note">
                  Edits to the specimen site will also update the specimen site
                  in the gross description. Click Save &amp; Continue Editing to
                  make additional changes to the gross description.
                </p>
              )}
              {(adminCase?.accessioning_lab?.settings?.materials_received
                ?.active ||
                specimen?.materials_received) && (
                <Field name="materials_received">
                  {({ field }: any) => (
                    <TextareaInput
                      label="Materials Received"
                      id="materials_received"
                      rows={4}
                      {...field}
                    />
                  )}
                </Field>
              )}
            </fieldset>

            {adminCase.source === CaseAdmin.source.PW && (
              <>
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Clinical ICD-10 Code
                  </legend>
                  {!!values.icd10codes?.length &&
                    values.icd10codes.map((c, idx) => {
                      return (
                        <Field
                          name={`icd10codes[${idx}]`}
                          key={`${c.code}${idx}`}
                        >
                          {({ field, form }: any) => (
                            <div className="panelContainer__inputGroup">
                              <AdminFieldIcd10Code
                                id={`icd10codes[${idx}]`}
                                idx={idx}
                                {...field}
                                onChange={(
                                  newValue: Icd10Code,
                                  idx: number
                                ) => {
                                  const updatedValues = [
                                    ...(values.icd10codes || []),
                                  ];
                                  updatedValues[idx] = newValue;
                                  form.setFieldValue(
                                    "icd10codes",
                                    updatedValues
                                  );
                                }}
                                disabled={disableSubmit}
                              />
                              <PathologyWatchButton
                                color="red"
                                onClick={() => {
                                  form.setFieldValue("icd10codes", [
                                    ...(values.icd10codes || []).slice(0, idx),
                                    ...(values.icd10codes || []).slice(idx + 1),
                                  ]);
                                }}
                              >
                                <FontAwesomeIcon icon="xmark" />
                              </PathologyWatchButton>
                            </div>
                          )}
                        </Field>
                      );
                    })}
                  <PathologyWatchButton
                    onClick={() => {
                      setFieldValue("icd10codes", [
                        ...(values.icd10codes || []),
                        { code: "" },
                      ]);
                    }}
                  >
                    + Add ICD-10 Code
                  </PathologyWatchButton>
                </fieldset>
                <SpecimenFormProtocols
                  specimen={values}
                  setFieldValue={setFieldValue}
                  accessioningLab={adminCase.accessioning_lab}
                />
              </>
            )}
          </div>
          <footer>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </PathologyWatchButton>
              {specimen?.id && (
                <PathologyWatchButton
                  color="red"
                  onClick={() => setDelSpecimen(true)}
                  disabled={disableSubmit}
                >
                  Delete
                </PathologyWatchButton>
              )}
            </div>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  resetForm();
                }}
                disabled={!dirty}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                onClick={() => {
                  setKeepEditing(true);
                }}
                type="submit"
                color="green"
                disabled={!dirty || disableSubmit}
              >
                Save &amp; Continue Editing
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="green"
                disabled={!dirty || disableSubmit}
              >
                Save
              </PathologyWatchButton>
            </div>
          </footer>
        </Form>
      )}
    </Formik>
  );
};

export default SpecimenForm;
